"use client"

import { useState, useEffect } from 'react'
import { Phone, PhoneCall, Menu, X, ArrowRight, BrainCircuit, Users, Mail } from "lucide-react"
import SectionWithStars from "./components/ui/SectionWithStars"
import AiSalesmanProfilePic from './assets/AI Salesman - Mini Profile Pic.png'
import AiReceptionistProfilePic from './assets/AI Receptionist - Mini Profile Pic.png'
import AiAnalystProfilePic from './assets/AI Analyst - Mini Profile Pic.png'
import GradientText from './components/GradientText'
import SpotlightCard from './components/ui/SpotlightCard'

function App() {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submissionStatus, setSubmissionStatus] = useState<null | 'success' | 'error'>(null);
  const [formData, setFormData] = useState({
    fullName: '',
    email: '',
    company: '',
    service: '',
  });

  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false)

  const navButtonStyle = {
    color: '#ffffff',
    background: 'none' as 'none',
    border: 'none',
    cursor: 'pointer',
    fontSize: '16px',
    transition: 'color 0.2s'
  };

  const buttonStyle = {
    width: '100%',
    backgroundColor: '#8b5cf6',
    color: 'white',
    padding: '12px',
    borderRadius: '9999px',
    fontWeight: '600',
    border: 'none',
    cursor: 'pointer',
    transition: 'all 0.2s'
  };

  const baseCardStyle: React.CSSProperties = {
    background: 'rgba(30, 41, 59, 0.35)',
    borderRadius: '16px',
    boxShadow: '0 4px 30px rgba(0, 0, 0, 0.1)',
    backdropFilter: 'blur(3.5px)',
    WebkitBackdropFilter: 'blur(3.5px)',
    border: '1px solid rgba(255, 255, 255, 1)',
    padding: '32px',
    transition: 'all 0.3s',
    height: '100%',
  };

  const descriptionTextStyle = {
    fontSize: '20px',
    color: '#ffffff',
    maxWidth: '600px',
    margin: '0 auto'
  };

  const cardDescriptionTextStyle = {
    color: '#ffffff',
    fontSize: '18px',
    lineHeight: '1.6',
    marginBottom: '24px'
  };

  const pillStyle = {
    backgroundColor: 'rgba(59, 130, 246, 0.1)',
    color: '#3b82f6',
    padding: '4px 12px',
    borderRadius: '9999px',
    fontSize: '14px'
  };

  const cardHeaderStyle = {
    display: 'flex',
    flexDirection: 'column' as 'column',
    alignItems: 'center',
    marginBottom: '24px'
  };

  const services = [
    {
      image: AiSalesmanProfilePic,
      alt: 'AI Salesman',
      title: 'Outbound Sales',
      description: (
        <div style={cardDescriptionTextStyle}>
          <p style={{ fontWeight: 'bold' }}>Focus your sales team on closing, not cold calling.</p>
          <ul style={{ listStyleType: 'disc', paddingLeft: '20px', marginTop: '24px', marginBottom: '24px', fontSize: '16px', lineHeight: '1.5' }}>
            <li style={{ marginBottom: '6px' }}>100 outbound calls per day, per agent</li>
            <li style={{ marginBottom: '6px' }}>Qualify leads based on your personal qualification criteria</li>
            <li>Just upload your CSV or list, and our agent will do the rest</li>
          </ul>
        </div>
      ),
      pills: ['100 calls/day', 'Lead qualification', 'CRM integration']
    },
    {
      image: AiReceptionistProfilePic,
      alt: 'AI Receptionist',
      title: '24/7 Receptionist',
      description: (
        <div style={cardDescriptionTextStyle}>
          <p style={{ fontWeight: 'bold' }}>Too many warm leads?</p>
          <ul style={{ listStyleType: 'disc', paddingLeft: '20px', marginTop: '24px', marginBottom: '24px', fontSize: '16px', lineHeight: '1.5' }}>
            <li style={{ marginBottom: '6px' }}>Takes a message when your humans are busy</li>
            <li style={{ marginBottom: '6px' }}>Smart handover from AI Sales Agent (no need for client to repeat details)</li>
            <li>Post call summary emailed over</li>
          </ul>
        </div>
      ),
      pills: ['24/7 availability', 'Message taking', 'Call routing']
    },
    {
      image: AiAnalystProfilePic,
      alt: 'AI Data Analyst',
      title: 'Data Analyst',
      description: (
        <div style={cardDescriptionTextStyle}>
          <p style={{ fontWeight: 'bold' }}>Curious how your calls are performing?</p>
          <ul style={{ listStyleType: 'disc', paddingLeft: '20px', marginTop: '24px', marginBottom: '24px', fontSize: '16px', lineHeight: '1.5' }}>
            <li style={{ marginBottom: '6px' }}>All in one Analytics Dashboard</li>
            <li style={{ marginBottom: '6px' }}>Live Call Monitoring + Live Analysis</li>
            <li>A/B Testing Campaigns</li>
          </ul>
        </div>
      ),
      pills: ['Analytics Dashboard', 'Live Monitoring', 'A/B Testing']
    }
  ];

  const scrollToSection = (sectionId: string) => {
    const element = document.getElementById(sectionId)
    if (element) {
      element.scrollIntoView({ behavior: "smooth" })
    }
    setIsMobileMenuOpen(false)
  }

  const handleFormChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleFormSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmissionStatus(null);

    try {
      const response = await fetch('https://formspree.io/f/mnnvgbze', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(formData),
      });

      if (response.ok) {
        setSubmissionStatus('success');
        setFormData({
          fullName: '',
          email: '',
          company: '',
          service: '',
        });
      } else {
        setSubmissionStatus('error');
      }
    } catch (error) {
      console.error('Submission error:', error);
      setSubmissionStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  }

  return (
    <div style={{ 
      backgroundColor: '#0f172a', 
      color: '#f8fafc', 
      fontFamily: '"Lemon Milk", sans-serif' 
    }}>
        
        {/* Sticky Header */}
        <header style={{ 
          position: 'fixed', 
          top: 0, 
          left: 0, 
          right: 0, 
          zIndex: 40, 
          backgroundColor: 'rgba(15, 23, 42, 0.9)', 
          backdropFilter: 'blur(16px)', 
          borderBottom: '1px solid #334155' 
        }}>
          <div style={{ 
            maxWidth: '1200px', 
            margin: '0 auto', 
            padding: '16px', 
            display: 'flex', 
            alignItems: 'center', 
            justifyContent: 'space-between' 
          }}>
            <div style={{ 
              display: 'flex', 
              alignItems: 'center', 
              gap: '12px' 
            }}>
              <img 
                src="/assets/Nocturne Agents Logo.png" 
                alt="Nocturne Agents Logo" 
                style={{ 
                  height: '48px', 
                  width: 'auto' 
                }} 
              />
              <div style={{ 
                fontSize: '36px', 
                fontWeight: 'bold', 
                fontFamily: 'Limelight, serif',
                background: 'linear-gradient(135deg, #3b82f6 0%, #8b5cf6 100%)', 
                WebkitBackgroundClip: 'text', 
                WebkitTextFillColor: 'transparent',
                backgroundClip: 'text'
              }}>
                Nocturne Agents
              </div>
            </div>
            
            {/* Desktop Navigation */}
            <nav style={{ display: 'flex', alignItems: 'center', gap: '24px' }}>
              <button
                onClick={() => scrollToSection("demo-video")}
                style={navButtonStyle}
                onMouseOver={(e) => e.currentTarget.style.color = '#d1d5db'}
                onMouseOut={(e) => e.currentTarget.style.color = '#ffffff'}
              >
                Demo
              </button>
              <button
                onClick={() => scrollToSection("services")}
                style={navButtonStyle}
                onMouseOver={(e) => e.currentTarget.style.color = '#d1d5db'}
                onMouseOut={(e) => e.currentTarget.style.color = '#ffffff'}
              >
                Services
              </button>
              <button
                onClick={() => scrollToSection("pricing")}
                style={navButtonStyle}
                onMouseOver={(e) => e.currentTarget.style.color = '#d1d5db'}
                onMouseOut={(e) => e.currentTarget.style.color = '#ffffff'}
              >
                Pricing
              </button>
              <button
                onClick={() => scrollToSection("footer")}
                style={navButtonStyle}
                onMouseOver={(e) => e.currentTarget.style.color = '#d1d5db'}
                onMouseOut={(e) => e.currentTarget.style.color = '#ffffff'}
              >
                Contact
              </button>
              <button
                onClick={() => scrollToSection("contact")}
                style={{ 
                  backgroundColor: '#8b5cf6', 
                  color: 'white', 
                  padding: '8px 24px', 
                  borderRadius: '9999px', 
                  fontWeight: '600',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  border: 'none',
                  cursor: 'pointer',
                  fontSize: '16px',
                  transition: 'all 0.2s'
                }}
                onMouseOver={(e) => e.currentTarget.style.backgroundColor = '#7c3aed'}
                onMouseOut={(e) => e.currentTarget.style.backgroundColor = '#8b5cf6'}
              >
                Join Waitlist
                <ArrowRight style={{ width: '16px', height: '16px' }} />
              </button>
            </nav>
          </div>
        </header>

        {/* Hero Section */}
        <SectionWithStars sectionId="hero" style={{ paddingTop: '128px', paddingBottom: '80px' }}>
          <div style={{ 
            maxWidth: '1200px', 
            margin: '0 auto', 
            textAlign: 'center', 
            position: 'relative' 
          }}>
            <h1 style={{ 
              fontSize: 'clamp(3rem, 8vw, 4.5rem)', 
              fontWeight: 'bold', 
              marginBottom: '32px', 
              lineHeight: '1.1' 
            }}>
              Turn a Simple List Into{" "}
              <GradientText colors={["#3b82f6", "#8b5cf6", "#3b82f6"]} animationSpeed={3}>
                Qualified Leads
              </GradientText>
            </h1>
            
            <p style={{ 
              fontSize: 'clamp(1.25rem, 4vw, 1.5rem)', 
              color: '#ffffff', 
              marginBottom: '48px', 
              lineHeight: '1.6', 
              maxWidth: '800px', 
              margin: '0 auto 48px auto' 
            }}>
              Our AI Sales Team will make up to{" "}
              <GradientText
                colors={["#3b82f6", "#8b5cf6", "#3b82f6"]}
                animationSpeed={3}
              >
                100 calls a day per agent
              </GradientText>, sending only qualified leads straight to your door.
            </p>
            
            <div style={{ 
              display: 'flex', 
              flexDirection: window.innerWidth < 640 ? 'column' : 'row',
              gap: '16px', 
              justifyContent: 'center', 
              alignItems: 'center' 
            }}>
              <button
                onClick={() => scrollToSection("contact")}
                style={{ 
                  backgroundColor: '#8b5cf6', 
                  color: 'white', 
                  padding: '16px 32px', 
                  borderRadius: '9999px', 
                  fontSize: '18px', 
                  fontWeight: '600',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  border: 'none',
                  cursor: 'pointer',
                  boxShadow: '0 10px 25px rgba(139, 92, 246, 0.3)',
                  transition: 'all 0.2s'
                }}
                onMouseOver={(e) => {
                  e.currentTarget.style.transform = 'scale(1.05)'
                  e.currentTarget.style.backgroundColor = '#7c3aed'
                }}
                onMouseOut={(e) => {
                  e.currentTarget.style.transform = 'scale(1)'
                  e.currentTarget.style.backgroundColor = '#8b5cf6'
                }}
              >
                Join Waitlist
                <Phone style={{ width: '20px', height: '20px' }} />
              </button>
            </div>
          </div>
        </SectionWithStars>

        {/* Demo Video Section */}
        <SectionWithStars sectionId="demo-video" style={{ backgroundColor: 'rgba(51, 65, 85, 0.3)' }}>
          <div style={{ 
            maxWidth: '1200px', 
            margin: '0 auto', 
            textAlign: 'center',
            position: 'relative'
          }}>
            <h2 style={{ 
              fontSize: 'clamp(2.5rem, 6vw, 3rem)', 
              fontWeight: 'bold', 
              marginBottom: '24px' 
            }}>
              See How It Works in{" "}
              <GradientText colors={["#3b82f6", "#8b5cf6", "#3b82f6"]} animationSpeed={3}>
                60 Seconds
              </GradientText>
            </h2>
            
            <div style={{ 
              position: 'relative', 
              maxWidth: '800px', 
              margin: '0 auto' 
            }}>
              <video 
                controls 
                autoPlay 
                loop 
                muted
                style={{ 
                  width: '100%', 
                  borderRadius: '16px', 
                  boxShadow: '0 25px 50px rgba(0, 0, 0, 0.25)', 
                  border: '1px solid #334155' 
                }}
                poster="https://placehold.co/800x450/1e293b/3b82f6?text=AI+Cold+Calling+Demo"
              >
                <source src="/assets/Sales Agent - Visual - DRAFT.mp4" type="video/mp4" />
                Your browser does not support the video tag.
              </video>
            </div>
          </div>
        </SectionWithStars>

        {/* Services Section */}
        <SectionWithStars sectionId="services">
          <div style={{ maxWidth: '1200px', margin: '0 auto', position: 'relative' }}>
            <div style={{ textAlign: 'center', marginBottom: '64px' }}>
              <h2 style={{ 
                fontSize: 'clamp(2.5rem, 6vw, 3rem)', 
                fontWeight: 'bold', 
                marginBottom: '24px' 
              }}>
                Your{" "}
                <GradientText colors={["#3b82f6", "#8b5cf6", "#3b82f6"]} animationSpeed={3}>
                  AI Driven
                </GradientText>
                {" "}Team
              </h2>
            </div>
            
            <div style={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'center', gap: '32px' }}>
              {services.map((service, index) => (
                <SpotlightCard key={index} className="rounded-lg" spotlightColor="rgba(59, 130, 246, 0.15)">
                  <div style={{
                    ...baseCardStyle,
                    flex: '1 1 45%',
                    minWidth: '350px',
                    maxWidth: '550px',
                    border: 'none'
                  }}>
                    <div style={cardHeaderStyle}>
                      <img 
                        src={service.image} 
                        alt={service.alt} 
                        style={{ 
                          width: '112px', 
                          height: '112px', 
                          borderRadius: '12px', 
                          marginBottom: '16px',
                          backgroundColor: 'rgba(139, 92, 246, 0.1)',
                          padding: '4px'
                        }} 
                      />
                      <h3 style={{ fontSize: '24px', fontWeight: 'bold', textAlign: 'center' }}>
                        <GradientText colors={["#3b82f6", "#8b5cf6", "#3b82f6"]} animationSpeed={3}>
                          {service.title}
                        </GradientText>
                      </h3>
                    </div>
                    {service.description}
                  </div>
                </SpotlightCard>
              ))}
            </div>
          </div>
        </SectionWithStars>

        {/* Pricing Section */}
        <SectionWithStars sectionId="pricing" style={{ backgroundColor: 'rgba(51, 65, 85, 0.3)' }}>
          <div style={{ maxWidth: '1200px', margin: '0 auto', position: 'relative' }}>
            <div style={{ textAlign: 'center', marginBottom: '64px' }}>
              <h2 style={{ 
                fontSize: 'clamp(2.5rem, 6vw, 3rem)', 
                fontWeight: 'bold', 
                marginBottom: '24px' 
              }}>
                Flexible{" "}
                <GradientText colors={["#3b82f6", "#8b5cf6", "#3b82f6"]} animationSpeed={3}>
                  Credit Plans
                </GradientText>
              </h2>
            </div>
            
            <div style={{ 
              display: 'grid', 
              gridTemplateColumns: window.innerWidth < 768 ? '1fr' : 'repeat(3, 1fr)',
              gap: '32px' 
            }}>
              {/* Starter Plan */}
              <SpotlightCard className="rounded-lg" spotlightColor="rgba(59, 130, 246, 0.15)">
                <div style={{
                  ...baseCardStyle,
                  textAlign: 'center',
                }}>
                  <h3 style={{ fontSize: '24px', fontWeight: 'bold', marginBottom: '16px' }}>Starter</h3>
                  <div style={{ fontSize: '48px', fontWeight: 'bold', margin: '16px 0' }}>
                    <GradientText colors={["#3b82f6", "#8b5cf6", "#3b82f6"]} animationSpeed={3}>
                      1,000
                    </GradientText>
                  </div>
                  <div style={{ color: '#ffffff', marginBottom: '24px' }}>Credits</div>
                  <p style={{ color: '#ffffff', marginBottom: '32px' }}>Perfect for initial campaigns</p>
                  <button
                    onClick={() => scrollToSection("contact")}
                    style={buttonStyle}
                    onMouseOver={(e) => e.currentTarget.style.backgroundColor = '#7c3aed'}
                    onMouseOut={(e) => e.currentTarget.style.backgroundColor = '#8b5cf6'}
                  >
                    Get Started
                  </button>
                </div>
              </SpotlightCard>

              {/* Growth Plan */}
              <SpotlightCard className="rounded-lg" spotlightColor="rgba(59, 130, 246, 0.15)">
                <div style={{
                  ...baseCardStyle,
                  textAlign: 'center',
                  border: '2px solid #8b5cf6',
                }}>
                  <h3 style={{ fontSize: '24px', fontWeight: 'bold', marginBottom: '16px' }}>Growth</h3>
                  <div style={{ fontSize: '48px', fontWeight: 'bold', margin: '16px 0' }}>
                    <GradientText colors={["#3b82f6", "#8b5cf6", "#3b82f6"]} animationSpeed={3}>
                      5,000
                    </GradientText>
                  </div>
                  <div style={{ color: '#ffffff', marginBottom: '24px' }}>Credits</div>
                  <p style={{ color: '#ffffff', marginBottom: '32px' }}>Best value for growing businesses</p>
                  <button
                    onClick={() => scrollToSection("contact")}
                    style={buttonStyle}
                    onMouseOver={(e) => e.currentTarget.style.backgroundColor = '#7c3aed'}
                    onMouseOut={(e) => e.currentTarget.style.backgroundColor = '#8b5cf6'}
                  >
                    Get Started
                  </button>
                </div>
              </SpotlightCard>

              {/* Scale Plan */}
              <SpotlightCard className="rounded-lg" spotlightColor="rgba(59, 130, 246, 0.15)">
                <div style={{
                  ...baseCardStyle,
                  textAlign: 'center',
                }}>
                  <h3 style={{ fontSize: '24px', fontWeight: 'bold', marginBottom: '16px' }}>Scale</h3>
                  <div style={{ fontSize: '48px', fontWeight: 'bold', margin: '16px 0' }}>
                    <GradientText colors={["#3b82f6", "#8b5cf6", "#3b82f6"]} animationSpeed={3}>
                      15,000
                    </GradientText>
                  </div>
                  <div style={{ color: '#ffffff', marginBottom: '24px' }}>Credits</div>
                  <p style={{ color: '#ffffff', marginBottom: '32px' }}>For high-volume outreach</p>
                  <button
                    onClick={() => scrollToSection("contact")}
                    style={buttonStyle}
                    onMouseOver={(e) => e.currentTarget.style.backgroundColor = '#7c3aed'}
                    onMouseOut={(e) => e.currentTarget.style.backgroundColor = '#8b5cf6'}
                  >
                    Get Started
                  </button>
                </div>
              </SpotlightCard>
            </div>
          </div>
        </SectionWithStars>

        {/* Contact Form Section */}
        <SectionWithStars sectionId="contact">
          <div style={{ maxWidth: '800px', margin: '0 auto', position: 'relative' }}>
            <div style={{ textAlign: 'center', marginBottom: '48px' }}>
              <h2 style={{ 
                fontSize: 'clamp(2.5rem, 6vw, 3rem)', 
                fontWeight: 'bold', 
                marginBottom: '24px' 
              }}>
                Join Our{" "}
                <GradientText colors={["#3b82f6", "#8b5cf6", "#3b82f6"]} animationSpeed={3}>
                  Waitlist
                </GradientText>
              </h2>
            </div>
            
            <div style={{ 
              backgroundColor: '#1e293b', 
              padding: '32px', 
              borderRadius: '16px', 
              border: '1px solid #334155',
              boxShadow: '0 10px 25px rgba(0, 0, 0, 0.1)'
            }}>
              <form onSubmit={handleFormSubmit} style={{ display: 'flex', flexDirection: 'column', gap: '24px' }}>
                <div style={{ 
                  display: 'grid', 
                  gridTemplateColumns: window.innerWidth < 768 ? '1fr' : '1fr 1fr',
                  gap: '24px' 
                }}>
                  <div>
                    <label htmlFor="fullName" style={{ 
                      display: 'block', 
                      fontSize: '14px', 
                      fontWeight: '500', 
                      marginBottom: '8px' 
                    }}>
                      Full Name *
                    </label>
                    <input
                      type="text"
                      id="fullName"
                      name="fullName"
                      required
                      placeholder="John Doe"
                      value={formData.fullName}
                      onChange={handleFormChange}
                      style={{ 
                        width: '100%', 
                        padding: '12px 16px', 
                        backgroundColor: '#0f172a', 
                        border: '1px solid #334155', 
                        borderRadius: '12px',
                        color: '#f8fafc',
                        fontSize: '16px',
                        outline: 'none',
                        transition: 'border-color 0.2s'
                      }}
                      onFocus={(e) => e.currentTarget.style.borderColor = '#8b5cf6'}
                      onBlur={(e) => e.currentTarget.style.borderColor = '#334155'}
                    />
                  </div>
                  <div>
                    <label htmlFor="email" style={{ 
                      display: 'block', 
                      fontSize: '14px', 
                      fontWeight: '500', 
                      marginBottom: '8px' 
                    }}>
                      Company Email *
                    </label>
                    <input
                      type="email"
                      id="email"
                      name="email"
                      required
                      placeholder="john@company.com"
                      value={formData.email}
                      onChange={handleFormChange}
                      style={{ 
                        width: '100%', 
                        padding: '12px 16px', 
                        backgroundColor: '#0f172a', 
                        border: '1px solid #334155', 
                        borderRadius: '12px',
                        color: '#f8fafc',
                        fontSize: '16px',
                        outline: 'none',
                        transition: 'border-color 0.2s'
                      }}
                      onFocus={(e) => e.currentTarget.style.borderColor = '#8b5cf6'}
                      onBlur={(e) => e.currentTarget.style.borderColor = '#334155'}
                    />
                  </div>
                </div>
                
                <div>
                  <label htmlFor="company" style={{ 
                    display: 'block', 
                    fontSize: '14px', 
                    fontWeight: '500', 
                    marginBottom: '8px' 
                  }}>
                    Company Name
                  </label>
                  <input
                    type="text"
                    id="company"
                    name="company"
                    placeholder="Your Company Inc."
                    value={formData.company}
                    onChange={handleFormChange}
                    style={{ 
                      width: '100%', 
                      padding: '12px 16px', 
                      backgroundColor: '#0f172a', 
                      border: '1px solid #334155', 
                      borderRadius: '12px',
                      color: '#f8fafc',
                      fontSize: '16px',
                      outline: 'none',
                      transition: 'border-color 0.2s'
                    }}
                    onFocus={(e) => e.currentTarget.style.borderColor = '#8b5cf6'}
                    onBlur={(e) => e.currentTarget.style.borderColor = '#334155'}
                  />
                </div>
                
                <div>
                  <label htmlFor="service" style={{ 
                    display: 'block', 
                    fontSize: '14px', 
                    fontWeight: '500', 
                    marginBottom: '8px' 
                  }}>
                    Which service are you interested in?
                  </label>
                  <select
                    id="service"
                    name="service"
                    value={formData.service}
                    onChange={handleFormChange}
                    style={{ 
                      width: '100%', 
                      padding: '12px 16px', 
                      backgroundColor: '#0f172a', 
                      border: '1px solid #334155', 
                      borderRadius: '12px',
                      color: '#f8fafc',
                      fontSize: '16px',
                      outline: 'none',
                      transition: 'border-color 0.2s'
                    }}
                    onFocus={(e) => e.currentTarget.style.borderColor = '#8b5cf6'}
                    onBlur={(e) => e.currentTarget.style.borderColor = '#334155'}
                  >
                    <option value="">Select a service</option>
                    <option value="AI Cold Caller">AI Cold Caller</option>
                    <option value="AI Receptionist">AI Receptionist</option>
                    <option value="Both">Both</option>
                  </select>
                </div>
                
                <button
                  type="submit"
                  disabled={isSubmitting}
                  style={{ 
                    width: '100%', 
                    backgroundColor: '#8b5cf6', 
                    color: 'white', 
                    padding: '16px', 
                    borderRadius: '12px', 
                    fontSize: '18px', 
                    fontWeight: '600',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: '8px',
                    border: 'none',
                    cursor: isSubmitting ? 'not-allowed' : 'pointer',
                    boxShadow: '0 4px 12px rgba(139, 92, 246, 0.3)',
                    transition: 'all 0.2s',
                    opacity: isSubmitting ? 0.7 : 1,
                  }}
                  onMouseOver={(e) => {
                    if (!isSubmitting) {
                      e.currentTarget.style.transform = 'scale(1.02)'
                      e.currentTarget.style.backgroundColor = '#7c3aed'
                    }
                  }}
                  onMouseOut={(e) => {
                    if (!isSubmitting) {
                      e.currentTarget.style.transform = 'scale(1)'
                      e.currentTarget.style.backgroundColor = '#8b5cf6'
                    }
                  }}
                >
                  {isSubmitting ? 'Submitting...' : 'Join Waitlist'}
                  <Phone style={{ width: '20px', height: '20px' }} />
                </button>
                
                {submissionStatus === 'success' && (
                  <p style={{ color: '#10b981', textAlign: 'center' }}>
                    Successfully submitted! We'll be in touch soon.
                  </p>
                )}
                {submissionStatus === 'error' && (
                  <p style={{ color: '#ef4444', textAlign: 'center' }}>
                    Something went wrong. Please try again.
                  </p>
                )}
              </form>
            </div>
          </div>
        </SectionWithStars>

        {/* Footer */}
        <footer id="footer" style={{ padding: '48px 16px', borderTop: '1px solid #334155', color: '#94a3b8' }}>
          <div style={{ maxWidth: '1200px', margin: '0 auto' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: '32px' }}>
                <div>
                    <p style={{ fontWeight: 'bold', color: '#f8fafc', fontSize: '18px' }}>Nocturne Agents Ltd</p>
                    <p style={{ marginTop: '16px', lineHeight: '1.6', maxWidth: '300px' }}>Lytchett House, 13 Freeland Park, Wareham Road, Poole, Dorset, BH16 6FA</p>
                    <p style={{ marginTop: '16px', lineHeight: '1.6', maxWidth: '300px' }}>Registered Company Number - 16525647</p>
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '16px', textAlign: 'left' }}>
                    <a href="mailto:support@nocturneagents.co.uk" style={{ color: '#f8fafc', textDecoration: 'none', display: 'flex', alignItems: 'center', gap: '8px' }}>
                        <Mail size={20} />
                        support@nocturneagents.co.uk
                    </a>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px', color: '#f8fafc' }}>
                        <Phone size={20} />
                        <span>TBD</span>
                    </div>
                </div>
            </div>
            <div style={{ textAlign: 'center', marginTop: '48px', paddingTop: '32px', borderTop: '1px solid #334155' }}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '12px', marginBottom: '16px' }}>
                    <img
                        src="/assets/Nocturne Agents Logo.png"
                        alt="Nocturne Agents Logo"
                        style={{
                            height: '28px',
                            width: 'auto'
                        }}
                    />
                    <div style={{
                        fontSize: '24px',
                        fontWeight: 'bold',
                        fontFamily: 'Limelight, serif',
                        background: 'linear-gradient(135deg, #3b82f6 0%, #8b5cf6 100%)',
                        WebkitBackgroundClip: 'text',
                        WebkitTextFillColor: 'transparent',
                        backgroundClip: 'text'
                    }}>
                        Nocturne Agents
                    </div>
                </div>
                <p style={{ fontSize: '14px' }}>© 2025 Nocturne Agents. All rights reserved.</p>
            </div>
          </div>
        </footer>
    </div>
  )
}

export default App